import { useState, useEffect } from "react";
import { X, Plus } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { SermonMetadata as SermonMetadataType } from "@/types";
import { DocumentTagPicker } from "@/components/tags/DocumentTagPicker";
import { AISuggestions } from "@/components/editor/AISuggestions";
import { useToast } from "@/hooks/use-toast";

interface SermonMetadataProps {
  isOpen: boolean;
  onClose: () => void;
  metadata: SermonMetadataType | null;
  onSave: (metadata: SermonMetadataType) => void | Promise<void>;
}

/** HTML date inputs need yyyy-MM-dd; DB may return ISO timestamps. */
function toDateInputValue(value?: string | null): string {
  if (!value) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 10);
}

export function SermonMetadataDialog({
  isOpen,
  onClose,
  metadata,
  onSave,
}: SermonMetadataProps) {
  const { toast } = useToast();
  const [formData, setFormData] = useState<SermonMetadataType>({
    document_id: metadata?.document_id ?? 0,
  });
  const [scriptures, setScriptures] = useState<string[]>([]);
  const [newScripture, setNewScripture] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setFormData({
      document_id: metadata?.document_id ?? 0,
      id: metadata?.id,
      series_name: metadata?.series_name ?? "",
      delivery_date: toDateInputValue(metadata?.delivery_date),
      audience: metadata?.audience ?? "",
      main_point: metadata?.main_point ?? "",
      notes: metadata?.notes ?? "",
      scriptures: metadata?.scriptures ?? [],
    });
    setScriptures(metadata?.scriptures || []);
    setNewScripture("");
  }, [metadata, isOpen]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleAddScripture = () => {
    if (newScripture.trim()) {
      setScriptures([...scriptures, newScripture.trim()]);
      setNewScripture("");
    }
  };

  const handleRemoveScripture = (index: number) => {
    const updated = [...scriptures];
    updated.splice(index, 1);
    setScriptures(updated);
  };

  const handleSave = async () => {
    if (!formData.document_id) {
      toast({
        title: "Cannot save",
        description: "Document is not ready yet.",
        variant: "destructive",
      });
      return;
    }

    setSaving(true);
    try {
      await onSave({
        ...formData,
        delivery_date: formData.delivery_date || undefined,
        scriptures,
        tags: undefined,
      });
      toast({ title: "Sermon metadata saved" });
      onClose();
    } catch (err) {
      toast({
        title: "Could not save metadata",
        description: err instanceof Error ? err.message : "Please try again.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Edit Sermon Metadata</DialogTitle>
        </DialogHeader>

        <div className="grid gap-4 py-4 max-h-[70vh] overflow-y-auto pr-2">
          <div className="grid grid-cols-1 gap-2">
            <Label htmlFor="series_name">Series Name</Label>
            <Input
              id="series_name"
              name="series_name"
              value={formData.series_name || ""}
              onChange={handleInputChange}
            />
          </div>

          <div className="grid grid-cols-1 gap-2">
            <Label htmlFor="delivery_date">Delivery Date</Label>
            <Input
              id="delivery_date"
              name="delivery_date"
              type="date"
              value={toDateInputValue(formData.delivery_date)}
              onChange={handleInputChange}
            />
          </div>

          <div className="grid grid-cols-1 gap-2">
            <Label htmlFor="audience">Audience</Label>
            <Input
              id="audience"
              name="audience"
              value={formData.audience || ""}
              onChange={handleInputChange}
            />
          </div>

          <div className="grid grid-cols-1 gap-2">
            <Label htmlFor="main_point">Main Point</Label>
            <Textarea
              id="main_point"
              name="main_point"
              rows={3}
              value={formData.main_point || ""}
              onChange={handleInputChange}
            />
          </div>

          {formData.document_id ? (
            <AISuggestions
              documentId={formData.document_id}
              currentMainPoint={formData.main_point}
              onUseMainPoint={(value) =>
                setFormData((prev) => ({ ...prev, main_point: value }))
              }
            />
          ) : null}

          <div className="grid grid-cols-1 gap-2">
            <Label>Scripture References</Label>
            <div className="space-y-2">
              {scriptures.map((scripture, index) => (
                <div key={`${scripture}-${index}`} className="flex items-center gap-2">
                  <Input value={scripture} readOnly />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => handleRemoveScripture(index)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              <div className="flex items-center gap-2">
                <Input
                  placeholder="e.g. John 3:16"
                  value={newScripture}
                  onChange={(e) => setNewScripture(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleAddScripture();
                    }
                  }}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="h-8 w-8"
                  onClick={handleAddScripture}
                  disabled={!newScripture.trim()}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-2">
            <Label>Tags</Label>
            {formData.document_id ? (
              <DocumentTagPicker documentId={formData.document_id} />
            ) : (
              <p className="text-sm text-muted-foreground">
                Save the document first, then add tags from Document details.
              </p>
            )}
          </div>

          <div className="grid grid-cols-1 gap-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              name="notes"
              rows={5}
              value={formData.notes || ""}
              onChange={handleInputChange}
            />
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="button" onClick={() => void handleSave()} disabled={saving}>
            {saving ? "Saving…" : "Save Metadata"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
